import { AsyncPipe, LowerCasePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SvgIconComponent } from 'angular-svg-icon';
import { Memoize } from 'typescript-memoize';
import { ButtonComponent } from '../../../../aptem-component-library/components/button/button.component';
import { PageUtils } from '../../common/page.utils';
import { TestletType } from '../../constants/testlet-type.enum';
import { OverviewCard } from '../../models/overview-card';
import { selectNextPage } from '../../store/selectors/page.selectors';
import { PageComponent } from '../common/page/page.component';

@Component({
  selector: 'overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss', '../testlets/styles/testlet.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, SvgIconComponent, AsyncPipe, PageComponent, ButtonComponent, LowerCasePipe],
})
export class OverviewComponent {
  assessmentCards = OverviewCard;
  protected pageState = this.store.select(selectNextPage);
  private nextPageName: string | TestletType;
  protected nextTestletType: TestletType = TestletType.Symbols;

  constructor(
    private router: Router,
    private store: Store,
    private pageUtils: PageUtils,
  ) {
    this.pageState.pipe(takeUntilDestroyed()).subscribe((nextPage) => {
      if (
        nextPage != null &&
        nextPage.page != null &&
        !nextPage.page.loading &&
        nextPage.page.error == null &&
        nextPage.page.data != null
      ) {
        this.nextPageName = nextPage.page.data;
        this.nextTestletType = this.getTestlet(this.nextPageName);
      } else {
        this.pageUtils.navigateToIntroductionPage(!nextPage?.page?.loading);
      }
    });
  }

  goToNextPage() {
    const url = this.pageUtils.buildParameterisedRoute(this.pageUtils.getNextPage(this.nextPageName));
    this.router.navigateByUrl(url);
  }

  @Memoize()
  protected getTestletList() {
    const nextTestlet = this.getTestlet(this.nextPageName);

    return Object.values(TestletType)
      .filter((t) => !isNaN(+t))
      .map((step) => {
        let progress = TestProgress.Current;
        let cardClass = 'current-card text-white';
        // @ts-ignore
        let currentTestletIndex = step * 1;
        let currentTestlet = this.getTestlet(step);
        if (currentTestletIndex < nextTestlet) {
          progress = TestProgress.Completed;
          cardClass = 'completed-card';
        } else if (currentTestletIndex > nextTestlet) {
          progress = TestProgress.NotCompleted;
          cardClass = 'not-completed-card';
        }
        const card: TestletCard = {
          key: currentTestletIndex,
          progress,
          cardClass,
          testletType: currentTestlet,
        };
        return card;
      });
  }

  protected calculateProgress(): string {
    const nextTestlet = this.getTestlet(this.nextPageName);
    return `${(100 / 7) * nextTestlet}%`;
  }

  private getTestlet(testName: TestletType | string): TestletType {
    switch (testName) {
      case TestletType.Symbols:
      case 'Symbols': {
        return TestletType.Symbols;
      }
      case TestletType.SimilarWords:
      case 'Similar Words': {
        return TestletType.SimilarWords;
      }
      case TestletType.NumberRecall:
      case 'Number Recall': {
        return TestletType.NumberRecall;
      }
      case TestletType.PictureCompletion:
      case 'Picture Completion': {
        return TestletType.PictureCompletion;
      }
      case TestletType.FakeWords:
      case 'Fake Words': {
        return TestletType.FakeWords;
      }
      case TestletType.MissingNumbers:
      case 'Missing Numbers': {
        return TestletType.MissingNumbers;
      }
      case TestletType.Objects:
      case 'Objects': {
        return TestletType.Objects;
      }
      case TestletType.ShapesAndColors:
      case 'Shapes & Colours': {
        return TestletType.ShapesAndColors;
      }
      default: {
        return TestletType.Symbols;
      }
    }
  }

  protected readonly TestletType = TestletType;
  protected readonly TestProgress = TestProgress;
  protected readonly Number = Number;
}

interface TestletCard {
  key: keyof string;
  progress: TestProgress;
  cardClass: string;
  testletType: TestletType;
}

enum TestProgress {
  NotCompleted = -1,
  Current = 0,
  Completed = 1,
}
